<template>
  <div class="srt-page">
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3">
        <div class="srt-top">
          <div>
            <div class="page-titlerow">
              <h2 class="srt-title">Stream routing</h2>
              <button type="button" class="page-helpbtn" :class="{ 'is-open': helpOpen }" :aria-expanded="helpOpen" @click="helpOpen = !helpOpen">
                <svg class="page-helpbtn__chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>How this page works
              </button>
            </div>
            <div class="srt-sub">Live matches only: every channel that should be on now, from its encoder stream to its origin.</div>
          </div>
          <div class="srt-clockbox">
            <div class="srt-clock">
              <span class="srt-clock__label">Now · {{ clockDate }}</span>
              <span class="srt-clock__time srt-mono">{{ clockTime }}</span>
            </div>
            <div class="srt-refresh">
              <label class="srt-refresh__toggle">
                <input type="checkbox" v-model="autoOn" @change="saveRefreshPrefs">
                Auto-refresh status
              </label>
              <div class="srt-seg" role="group" aria-label="Refresh interval" :class="{ 'is-off': !autoOn }">
                <button v-for="v in intervals" :key="v" type="button" class="srt-seg__btn" :class="{ 'is-on': interval === v }" :aria-pressed="interval === v" @click="pickInterval(v)">{{ v }}s</button>
              </div>
              <span class="srt-refresh__count srt-mono">{{ autoOn ? 'next in ' + countdown + 's' : 'paused' }}</span>
              <button type="button" class="srt-btn" :disabled="pollInFlight" @click="refreshNow">
                <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 4v5h-5"/></svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div v-show="helpOpen" class="page-help">
          <div class="page-help__sec">
            <div class="page-help__label">What is shown</div>
            <ul>
              <li>Only what B-API is running now: every active channel whose stream window is open (match start minus its lead time, to match end plus its trail time). Other matches of the day are not shown.</li>
              <li>For each of those matches, its channels that have an encoder and stream id. One line per channel, from the encoder stream to the origin it is sent to.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">Where the data comes from</div>
            <ul>
              <li>Everything except the origin output comes from <b>B-API's memory</b>, one request at the auto-refresh interval. This page sends nothing to the backend or the encoders.</li>
              <li><b>Plan</b> - B-API's calendar feeds: which channels should be on and how they are set up (origin, stream app, channel name, encoder stream). B-API reads the calendar every 30 seconds.</li>
              <li><b>Stream stats</b> - whether each stream runs, from B-API's read of each encoder (every 30 seconds).</li>
              <li><b>Output URLs</b> - where each stream is sent, as B-API last read it: when it started or took over the stream, when it saw it restart, and every minute while the stream runs with a wrong URL. A running stream B-API has not read yet shows as <b>output not checked yet</b>.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">What is flagged</div>
            <ul>
              <li><b>Wrong origin / wrong channel</b> - the stream runs, but its output URL differs from the origin, stream app or channel name in the calendar. The line goes where the stream really goes; a dotted line shows the expected route.</li>
              <li><b>No output URL</b> - the stream runs but has no output set on the encoder.</li>
              <li><b>Down</b> - the match is live but the stream is not running.</li>
              <li>This page only shows problems. To change a setup, open the match in Daily.</li>
            </ul>
          </div>
          <div class="page-help__sec">
            <div class="page-help__label">Stream Monitoring - Origin Output</div>
            <ul>
              <li>What players get from the origin, read from the stream monitor on every refresh (one request for all channels). One box per channel, on the origin the calendar expects.</li>
              <li><b>Green</b> - every variant is OK. <b>Yellow</b> - at least one variant has a problem. <b>Red</b> - every variant has a problem, or monitoring could not start (<b>Not started</b> means the origin has no stream for the channel). <b>Grey</b> - not monitored yet, monitor data older than 60 seconds, or the monitor could not be read.</li>
              <li>A variant has a problem when it is unreachable, frozen, or shows a black or white screen. Picture and frozen describe the latest checked frame, so a single bad frame shows here even if no Slack alert follows.</li>
              <li>The monitor starts checking a channel 2 minutes after its feed window opens and picks up new channels on its 5-minute calendar poll, so a channel can show <b>Not monitored yet</b> for up to about 7 minutes.</li>
              <li>Click a box to see every variant with its status, picture, frozen state and last check time.</li>
            </ul>
          </div>
        </div>

        <div v-if="loadError" class="error-message mt-3">{{ loadError }}</div>
        <div v-else-if="bapiNotice" class="error-message mt-3">{{ bapiNotice }}</div>

        <div class="srt-bar">
          <div class="srt-chips">
            <span v-for="c in summaryChips" :key="c.label" class="srt-chip"><span v-if="c.kind" class="srt-chip__dot" :class="'srt-k--' + c.kind"></span><b>{{ c.n }}</b> {{ c.label }}</span>
            <button v-if="errors.length" type="button" class="srt-chip srt-chip--err" :class="{ 'is-on': onlyErrors }" :aria-pressed="onlyErrors" @click="onlyErrors = !onlyErrors">
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l9.5 17h-19z"/><path d="M12 10v4M12 17.5h.01"/></svg>
              <b>{{ errors.length }}</b> don't match the calendar
            </button>
            <button v-if="pinned" type="button" class="srt-chip srt-chip--focus" @click="pinned = null">
              Focused: <b>{{ pinnedLabel }}</b>
              <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
            </button>
          </div>
        </div>

        <div v-if="matchChips.length" class="srt-matches">
          <span class="srt-matches__label">Live matches</span>
          <button v-for="m in matchChips" :key="m.id" type="button" class="srt-match" :class="{ 'is-pinned': isPinned('match', m.id), 'is-dim': m.dim }"
                  :aria-pressed="isPinned('match', m.id)" @mouseenter="hover = { k: 'match', id: m.id }" @mouseleave="hover = null" @click="pin({ k: 'match', id: m.id })">
            <span class="srt-match__dot" :class="'is-' + m.tone"></span>
            <b>{{ m.title }}</b>
            <span class="srt-mono srt-muted">{{ m.window }}</span>
            <span :class="{ 'srt-bad-text': m.tone === 'bad' }">{{ m.meta }}</span>
          </button>
        </div>
        <div v-else-if="!loading && !loadError" class="srt-muted mt-3">No live matches right now.</div>

        <section v-if="selErr" class="srt-errpanel" aria-label="Streams that don't match the calendar">
          <div class="srt-errpanel__head">
            <span class="srt-errpanel__ico"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l9.5 17h-19z"/><path d="M12 10v4M12 17.5h.01"/></svg></span>
            <span class="srt-errpanel__title">{{ errors.length === 1 ? '1 running stream doesn’t match the calendar' : errors.length + ' running streams don’t match the calendar' }}</span>
            <div class="srt-errpanel__tabs">
              <button v-for="e in errors" :key="e.key" type="button" class="srt-errtab" :class="{ 'is-on': selErr.key === e.key }" :aria-pressed="selErr.key === e.key" @click="pickError(e)">
                <span class="srt-mono">{{ e.expected.channel }}</span> · {{ errKindLabel(e) }}
              </button>
            </div>
            <span class="srt-errpanel__checked">{{ outputsLabel }}</span>
          </div>
          <div class="srt-errpanel__urls">
            <div class="srt-urlcol">
              <span class="srt-urlcol__label">Expected · from calendar</span>
              <div class="srt-url"><span v-for="(g, i) in panel.planSegs" :key="i" :class="{ 'srt-url__diff--plan': g.diff }">{{ g.t }}</span></div>
              <span class="srt-urlcol__note">{{ panel.planNote }}</span>
            </div>
            <div class="srt-urlcol">
              <span class="srt-urlcol__label srt-urlcol__label--live">Live on encoder · {{ selErr.encoder }} #{{ selErr.streamId }}</span>
              <div v-if="panel.liveSegs.length" class="srt-url srt-url--live"><span v-for="(g, i) in panel.liveSegs" :key="i" :class="{ 'srt-url__diff--live': g.diff }">{{ g.t }}</span></div>
              <div v-else class="srt-url srt-url--live srt-url--none">{{ panel.liveEmpty }}</div>
              <span class="srt-urlcol__note">{{ selErr.name }}</span>
            </div>
          </div>
          <div class="srt-errpanel__foot">
            <div>
              <div class="srt-errpanel__issue">{{ panel.issue }}</div>
              <div class="srt-muted">{{ panel.impact }}</div>
            </div>
            <div class="srt-errpanel__actions">
              <router-link class="srt-btn" :to="dailyLink(selErr.match)">Open match in Daily</router-link>
            </div>
          </div>
        </section>

        <div v-if="loading" class="srt-muted mt-3">Loading...</div>
        <StreamRoutingMap v-else
            :model="model"
            :focus="focus"
            :pinned="pinned"
            :only-errors="onlyErrors"
            :monitor-by-id="monitorById"
            :monitor="monitorState"
            :now-ms="now.getTime()"
            @hover="hover = $event"
            @pin="pin">
        </StreamRoutingMap>

        <div class="srt-sources">
          <div class="srt-source">
            <span class="srt-source__what">Plan, status and outputs · B-API memory</span>
            <span class="srt-mono">GET /calendar/state</span>
            <span class="srt-muted">{{ bapiLabel }}</span>
          </div>
          <div class="srt-source">
            <span class="srt-source__what">Stream monitoring · what players get</span>
            <span class="srt-mono">GET {{ monitorUrl }}</span>
            <span class="srt-muted">{{ monitorLabel }}</span>
          </div>
        </div>

        <div class="srt-legend">
          <span><i class="srt-legend__line srt-line-sample--live"></i>OK, live and matches the calendar</span>
          <span><i class="srt-legend__line srt-line-sample--checking"></i>Live, output not checked yet</span>
          <span><i class="srt-legend__line srt-line-sample--down"></i>Down, match live but stream not running</span>
          <span><i class="srt-legend__line srt-line-sample--err"></i>Running, doesn't match the calendar</span>
          <span><i class="srt-legend__line srt-line-sample--ghost"></i>Expected route, not arriving</span>
          <span class="srt-legend__hint">Hover to trace · click to pin</span>
        </div>
        <div class="srt-legend srt-legend--out">
          <span class="srt-legend__title">Origin output</span>
          <span><i class="srt-legend__dot srt-out--green"></i>All variants OK</span>
          <span><i class="srt-legend__dot srt-out--amber"></i>At least one variant has a problem</span>
          <span><i class="srt-legend__dot srt-out--red"></i>All variants have a problem, or monitoring could not start</span>
          <span><i class="srt-legend__dot srt-out--grey"></i>Not monitored yet, stale, or monitor not reachable</span>
          <span class="srt-legend__hint">Click a box for all variants</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from '@/components/Header.vue';
import StreamRoutingMap from '@/components/stream-routing/StreamRoutingMap.vue';
import XaccelService from '@/services/xaccel-service';
import MonitorService from '@/services/monitor-service';
import { encoderStateFromBapi } from '@/utils/bapi-functions';
import { toDateParam } from './daily/dailyDate';
import { bapiPlan, bapiOutputs, buildRoutes, streamMatches, urlSegments, outputStatus, ERROR_KINDS } from '@/utils/stream-routing';

const PREFS_KEY = 'streamRouting.refresh';
const hms = d => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-';
const hhmm = ms => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

function readPrefs() {
  try {
    const p = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    return { autoOn: p.autoOn !== false, interval: [10, 30, 60].includes(p.interval) ? p.interval : 30 };
  } catch (e) {
    return { autoOn: true, interval: 30 };
  }
}

// Read-only map of encoder streams -> channels -> origins, checked against the calendar, from B-API's memory.
export default {
  name: 'StreamRouting',
  components: { Header, StreamRoutingMap },
  data() {
    const prefs = readPrefs();
    return {
      helpOpen: false,
      monitorChannels: [],
      monitorState: { loaded: false, error: '' },
      monitorAt: null,
      monitorUrl: MonitorService.url,
      loading: true,
      loadError: '',
      // GET /calendar/state from B-API: feeds (plan + output URLs), encoder stats
      bapiState: null,
      bapiAt: null,
      pollInFlight: false,
      planNowMs: Date.now(),
      now: new Date(),
      autoOn: prefs.autoOn,
      interval: prefs.interval,
      intervals: [10, 30, 60],
      countdown: prefs.interval,
      hover: null,
      pinned: null,
      onlyErrors: false,
      errSelKey: null
    };
  },
  computed: {
    plan() {
      return bapiPlan(this.bapiState, this.planNowMs);
    },
    // The encoders of the plan's channels.
    distinctEncoders() {
      const seen = new Map();
      for (const ch of this.plan.channels) {
        const e = String(ch.encoder || '').trim();
        if (e && !seen.has(e.toLowerCase())) seen.set(e.toLowerCase(), e);
      }
      return [...seen.values()];
    },
    // Per encoder, from B-API's last read of it: { stats: Map|null, lastOkAt, unreachable, notRead }
    encoderState() {
      const out = {};
      for (const enc of this.distinctEncoders) {
        out[enc] = this.bapiState ? encoderStateFromBapi(this.bapiState.encoders?.[enc]) : { stats: null, lastOkAt: null, unreachable: false, pending: true };
      }
      return out;
    },
    // { [streamKey]: { url, route, at } }: each stream's output URL as B-API last read it
    outputs() {
      return bapiOutputs(this.bapiState);
    },
    // Why B-API's data may be missing or old; '' = all fine.
    bapiNotice() {
      const s = this.bapiState;
      if (!s) return '';
      if (s.state !== 'RUNNING') return `B-API calendar is ${s.state.toLowerCase().replace(/_/g, ' ')}: it has no plan, stream data or output URLs.`;
      if (s.lastTickAt && this.now.getTime() - Date.parse(s.lastTickAt) > 3 * 60 * 1000) {
        return `B-API calendar has not ticked since ${hms(new Date(s.lastTickAt))}: what is shown is old.`;
      }
      return '';
    },
    model() {
      return buildRoutes({
        planChannels: this.plan.channels,
        encoderState: this.encoderState,
        outputs: this.outputs
      });
    },
    streams() {
      return this.model.encoders.flatMap(e => e.streams);
    },
    errors() {
      return this.streams.filter(s => ERROR_KINDS.includes(s.kind));
    },
    focus() {
      return this.pinned || this.hover;
    },
    selErr() {
      if (!this.errors.length) return null;
      const pinnedKey = this.pinned && this.pinned.k === 'stream' ? this.pinned.id : null;
      return this.errors.find(e => e.key === pinnedKey)
        || this.errors.find(e => e.key === this.errSelKey)
        || this.errors[0];
    },
    panel() {
      const s = this.selErr;
      const out = { planSegs: [], liveSegs: [], planNote: '', liveEmpty: '', issue: '', impact: '' };
      if (!s) return out;
      const live = s.output && s.output.route;
      const exp = s.expected;
      const diff = live ? {
        origin: !this.same(exp.origin, live.origin),
        app: !!exp.app && !this.same(exp.app, live.app),
        channel: !this.same(exp.channel, live.channel)
      } : {};
      out.planSegs = urlSegments({ origin: exp.origin, app: exp.app, channel: exp.channel }, diff);
      out.planNote = `${s.match.title} · calChannelId ${s.plan.calChannelId} · origin + streamApp + channelName`;
      if (live) out.liveSegs = urlSegments(live, diff);
      else out.liveEmpty = s.output && s.output.url ? s.output.url : 'No output URL on the encoder stream';

      if (s.kind === 'nooutput') {
        out.issue = 'The encoder stream has no output URL.';
        out.impact = `${exp.channel} is running on the encoder but is not sent anywhere. Players on ${exp.origin} get no feed from this source.`;
      } else if (s.err === 'origin') {
        out.issue = `Output URL origin is ${live.origin}, the calendar has ${exp.origin}.`;
        out.impact = `${exp.channel} is live but not reaching ${exp.origin}. Players on ${exp.origin} get no ${s.match.title} feed from this source.`;
      } else if (s.err === 'route') {
        const bits = [];
        if (diff.channel) bits.push(`points to ${live.channel}, this channel is ${exp.channel}`);
        if (diff.app) bits.push(`uses app ${live.app}, the calendar has ${exp.app}`);
        out.issue = 'Output URL ' + bits.join(' and ') + '.';
        out.impact = `${exp.channel} gets no feed from this source; the stream goes out as ${live.app}/${live.channel}.`;
      }
      return out;
    },
    summaryChips() {
      const count = k => this.streams.filter(s => s.kind === k).length;
      const running = this.streams.filter(s => s.running).length;
      return [
        { n: this.plan.matches.length, label: this.plan.matches.length === 1 ? 'live match' : 'live matches' },
        { n: `${running}/${this.streams.length}`, label: 'channels running' },
        { n: count('live'), label: 'live OK', kind: 'live' },
        { n: count('down'), label: 'down', kind: 'down' },
        { n: this.countOutputs('green'), label: 'output OK', kind: 'out-green' },
        { n: this.countOutputs('amber'), label: 'output degraded', kind: 'out-amber' },
        { n: this.countOutputs('red'), label: 'no output', kind: 'out-red' }
      ];
    },
    monitorById() {
      const map = {};
      for (const c of this.monitorChannels) map[c.cal_channel_id] = c;
      return map;
    },
    // Origin output status per calendar channel (one per stream row)
    outputTones() {
      const nowMs = this.now.getTime();
      const tones = {};
      for (const s of this.streams) tones[s.key] = outputStatus(this.monitorById[s.plan.calChannelId], this.monitorState, nowMs).tone;
      return tones;
    },
    monitorLabel() {
      if (this.monitorState.error) return 'Not reachable: ' + this.monitorState.error;
      if (!this.monitorAt) return 'Loading...';
      return `${this.monitorChannels.length} ${this.monitorChannels.length === 1 ? 'channel' : 'channels'} monitored · last ` + hms(this.monitorAt) + ' · read on every refresh';
    },
    matchChips() {
      const filtering = !!this.focus || this.onlyErrors;
      return this.plan.matches.map((m) => {
        const rows = this.streams.filter(s => s.match && s.match.id === m.id);
        const bad = rows.filter(s => s.err || s.kind === 'down' || ['red', 'amber'].includes(this.outputTones[s.key])).length;
        const focused = this.focus && this.focus.k === 'match' && this.focus.id === m.id;
        const passes = rows.some(s => streamMatches(s, { focus: this.focus, onlyErrors: this.onlyErrors }));
        let meta = `${rows.length} ${rows.length === 1 ? 'channel' : 'channels'}` + (bad ? ` · ${bad} need attention` : ' · all OK');
        if (!m.channelCount) meta = 'no active channels';
        else if (m.unlinked) meta += ` · ${m.unlinked} without encoder stream`;
        return {
          id: m.id,
          title: m.title,
          window: hhmm(m.start) + '–' + hhmm(m.end),
          meta,
          tone: bad || !rows.length ? 'bad' : 'ok',
          dim: filtering && !focused && !passes
        };
      });
    },
    pinnedLabel() {
      const p = this.pinned;
      if (!p) return '';
      if (p.k === 'match') {
        const m = this.plan.matches.find(x => x.id === p.id);
        return m ? m.title : p.id;
      }
      if (p.k === 'stream') {
        const s = this.streams.find(x => x.key === p.id);
        if (!s) return p.id;
        return s.expected.channel + ' · ' + s.encoder + ' #' + s.streamId;
      }
      return p.id;
    },
    outputsLabel() {
      const s = this.selErr;
      const read = s && s.output && s.output.at;
      return read ? 'Output URL as B-API read it at ' + hms(read) : 'Output URL not read by B-API yet';
    },
    bapiLabel() {
      const last = this.bapiAt ? ' · last ' + hms(this.bapiAt) : '';
      return (this.autoOn ? `Every ${this.interval}s` : 'Auto-refresh off') + last;
    },
    clockTime() {
      return hms(this.now);
    },
    clockDate() {
      return this.now.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
    }
  },
  created() {
    this.loadAll();
  },
  mounted() {
    this._tick = setInterval(() => {
      this.now = new Date();
      if (document.hidden || !this.autoOn || this.loading) return;
      if (this.countdown > 1) {
        this.countdown--;
      } else {
        this.countdown = this.interval;
        this.reloadAndPoll();
      }
    }, 1000);
  },
  beforeUnmount() {
    clearInterval(this._tick);
  },
  methods: {
    same(a, b) {
      return String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
    },
    countOutputs(tone) {
      return Object.values(this.outputTones).filter(t => t === tone).length;
    },
    loadMonitor() {
      return MonitorService.getStatus().then((channels) => {
        this.monitorChannels = channels;
        this.monitorAt = new Date();
        this.monitorState = { loaded: true, error: '' };
      }).catch((err) => {
        console.error('StreamRouting: stream monitor failed', err);
        this.monitorState = { loaded: true, error: (err && err.message) || 'request failed' };
      });
    },
    loadAll() {
      this.loading = true;
      this.loadMonitor();
      this.loadBapi().finally(() => {
        this.loading = false;
      });
    },
    reloadAndPoll() {
      this.loadMonitor();
      return this.loadBapi();
    },
    // One B-API request for the plan, the stream stats and the output URLs (no encoder or backend call behind it).
    // On a failure the last answer stays, with the error shown.
    loadBapi() {
      if (this.pollInFlight) return Promise.resolve();
      this.pollInFlight = true;
      return XaccelService.getCalendarState().then((state) => {
        this.bapiState = state;
        this.bapiAt = new Date();
        this.planNowMs = Date.now();
        this.loadError = '';
      }).catch((error) => {
        console.error('StreamRouting: B-API calendar state failed', error);
        const msg = (error.response && error.response.data && error.response.data.message) || error.message || 'request failed';
        this.loadError = 'B-API not reachable: ' + msg + (this.bapiAt ? '. Showing what it sent at ' + hms(this.bapiAt) + '.' : '');
      }).finally(() => {
        this.pollInFlight = false;
      });
    },
    refreshNow() {
      this.countdown = this.interval;
      this.reloadAndPoll();
    },
    pickInterval(v) {
      this.interval = v;
      this.countdown = v;
      this.saveRefreshPrefs();
    },
    saveRefreshPrefs() {
      this.countdown = this.interval;
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify({ autoOn: this.autoOn, interval: this.interval }));
      } catch (e) {
        // Storage unavailable - the choice just isn't remembered.
      }
    },
    isPinned(k, id) {
      return !!this.pinned && this.pinned.k === k && this.pinned.id === id;
    },
    pin(f) {
      this.pinned = this.isPinned(f.k, f.id) ? null : f;
      if (this.pinned && f.k === 'stream' && this.errors.some(e => e.key === f.id)) this.errSelKey = f.id;
    },
    pickError(e) {
      this.errSelKey = e.key;
      this.pinned = { k: 'stream', id: e.key };
    },
    errKindLabel(e) {
      if (e.kind === 'nooutput') return 'no output URL';
      return e.err === 'origin' ? 'wrong origin' : 'wrong channel';
    },
    dailyLink(match) {
      return { path: '/configuration/daily', query: { date: toDateParam(new Date(match.start)), openMatch: match.id } };
    }
  }
};
</script>
<style src="../../assets/style.css"></style>
